import React, { useState, useEffect } from 'react';
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Collapse,
  IconButton,
} from '@mui/material';
import { Refresh as RefreshIcon, Edit as EditIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import axios from 'axios';
import { formatDate } from '../utils/dateFormat';

const SMSLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [editMobileNumber, setEditMobileNumber] = useState('');
  const [editError, setEditError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page, rowsPerPage]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = {
        page: page,
        page_size: rowsPerPage,
        ...(statusFilter && { status: statusFilter }),
      };
      const response = await axios.get('/sms/logs', { params });
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (err) {
      setError('Failed to load SMS logs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing page size
  };

  const handleRetry = async (smsId) => {
    try {
      const response = await axios.post(`/sms/retry/${smsId}`);
      if (response.data.success) {
        setSuccess('SMS resent successfully');
        setTimeout(() => setSuccess(''), 5000);
        loadLogs();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to retry SMS');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleEditClick = (log) => {
    setEditingLog(log);
    setEditMobileNumber(log.mobile_number);
    setEditError('');
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingLog(null);
    setEditMobileNumber('');
    setEditError('');
  };

  const handleEditSave = async () => {
    if (!editingLog) return;

    setEditError('');
    
    try {
      const response = await axios.put(`/sms/update-mobile/${editingLog.id}`, {
        mobile_number: editMobileNumber,
      });
      
      if (response.data.success) {
        setSuccess('Mobile number updated successfully. You can now retry sending the SMS.');
        setTimeout(() => setSuccess(''), 5000);
        handleEditClose();
        loadLogs();
      }
    } catch (err) {
      setEditError(err.response?.data?.detail || 'Failed to update mobile number');
    }
  };

  const isInvalidNumberError = (errorMessage) => {
    return errorMessage && errorMessage.toLowerCase().includes('invalid mobile number');
  };

  const handleToggleExpand = (logId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedRows(newExpanded);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      case 'retrying':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0); // Reset to first page when filter changes
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="retrying">Retrying</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadLogs}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tour</TableCell>
                <TableCell>Date & Time</TableCell>
                <TableCell>Participant</TableCell>
                <TableCell>Mobile Number</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sent At</TableCell>
                <TableCell>Error</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary">
                      No SMS logs found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => {
                  const isExpanded = expandedRows.has(log.id);
                  const canExpand = log.status === 'sent' && log.message;
                  
                  return (
                    <React.Fragment key={log.id}>
                      <TableRow 
                        sx={{ 
                          cursor: canExpand ? 'pointer' : 'default',
                          '&:hover': canExpand ? { backgroundColor: 'action.hover' } : {}
                        }}
                        onClick={() => canExpand && handleToggleExpand(log.id)}
                      >
                        <TableCell>
                          {canExpand && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleExpand(log.id);
                              }}
                              sx={{ mr: 1 }}
                            >
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          )}
                          {log.tour_name}
                        </TableCell>
                        <TableCell>
                          {log.tour_date} at {log.tour_time}
                        </TableCell>
                        <TableCell>{log.participant_name}</TableCell>
                        <TableCell>{log.mobile_number}</TableCell>
                        <TableCell>
                          <Chip
                            label={log.status}
                            color={getStatusColor(log.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {formatDate(log.sent_at)}
                        </TableCell>
                        <TableCell>
                          {log.error_message ? (
                            <Typography
                              variant="body2"
                              color="error"
                              sx={{
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={log.error_message}
                            >
                              {log.error_message}
                            </Typography>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {log.status === 'failed' && isInvalidNumberError(log.error_message) && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={<EditIcon />}
                                onClick={() => handleEditClick(log)}
                              >
                                Edit
                              </Button>
                            )}
                            {(log.status === 'failed' || log.status === 'pending') && 
                             !isInvalidNumberError(log.error_message) && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleRetry(log.id)}
                              >
                                Retry
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                      {canExpand && (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ py: 0, borderBottom: 0 }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  SMS Message:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: 'monospace',
                                    backgroundColor: 'white',
                                    p: 2,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  {log.message}
                                </Typography>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100, 250]}
            labelRowsPerPage="Rows per page:"
          />
        </TableContainer>
      )}

      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Mobile Number</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Participant: {editingLog?.participant_name}
            </Typography>
            <TextField
              fullWidth
              label="Mobile Number"
              value={editMobileNumber}
              onChange={(e) => setEditMobileNumber(e.target.value)}
              placeholder="e.g., 0412345678"
              error={!!editError}
              helperText={editError || 'Enter a valid Australian mobile number (10 digits, starting with 04)'}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SMSLogs;

